import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { IInvoice, IInvoiceReceipt, InvoiceStatusEnum, InvoiceTypeEnum } from '../../types';
import { getInvoiceReceipts, unlinkReceipt } from '../../services';
import {
	PLATFORM_LABEL,
	STATUS_COLOR,
	STATUS_LABEL,
	TYPE_COLOR,
	TYPE_LABEL,
	formatAmount,
	formatDate,
	isInvoiceOverdue,
} from '../../uiHelpers';

interface Props {
	invoice: IInvoice;
	canWrite: boolean;
	onIssue: (invoice: IInvoice) => void;
	onLink: (invoice: IInvoice) => void;
	onCreditNote: (invoice: IInvoice) => void;
	onDelete: (invoice: IInvoice) => void;
	onChanged: (invoice: IInvoice) => void;
}

export const InvoiceCard = ({
	invoice,
	canWrite,
	onIssue,
	onLink,
	onCreditNote,
	onDelete,
	onChanged,
}: Props): JSX.Element => {
	const [expanded, setExpanded] = useState(false);
	const [linkedReceipts, setLinkedReceipts] = useState<IInvoiceReceipt[]>([]);
	const [receiptToUnlink, setReceiptToUnlink] = useState<IInvoiceReceipt | null>(null);
	const [error, setError] = useState<string | null>(null);

	const isCredit = invoice.type === InvoiceTypeEnum.Credit;
	const isDraft = invoice.status === InvoiceStatusEnum.Draft;
	const isIssued = invoice.status === InvoiceStatusEnum.Issued;
	const overdue = isInvoiceOverdue(invoice);

	const canLink = isIssued && isCredit && invoice.pendingAmount > 0;
	const canCreditNote = isIssued && invoice.type !== InvoiceTypeEnum.CreditNote;

	const loadReceipts = useCallback(() => {
		if (!isCredit) return;
		getInvoiceReceipts({ invoiceId: invoice.id })
			.then(setLinkedReceipts)
			.catch(() => setLinkedReceipts([]));
	}, [invoice.id, isCredit]);

	useEffect(() => {
		if (expanded) loadReceipts();
	}, [expanded, loadReceipts]);

	const handleUnlink = async (): Promise<void> => {
		if (!receiptToUnlink) return;
		try {
			const updated = await unlinkReceipt(invoice.id, receiptToUnlink.id);
			setReceiptToUnlink(null);
			onChanged(updated);
			loadReceipts();
		} catch {
			setError('No se pudo desvincular el recibo.');
			setReceiptToUnlink(null);
		}
	};

	return (
		<Card variant="outlined">
			<CardContent sx={{ pb: 1 }}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
					<Box>
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography
								variant="subtitle1"
								sx={{ fontFamily: 'monospace', color: isDraft ? 'text.secondary' : 'text.primary' }}
							>
								{invoice.invoiceNumber}
							</Typography>
							<Chip label={TYPE_LABEL[invoice.type]} color={TYPE_COLOR[invoice.type]} size="small" />
							<Chip label={STATUS_LABEL[invoice.status]} color={STATUS_COLOR[invoice.status]} size="small" />
						</Stack>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{invoice.passengerName} · Emisión {formatDate(invoice.issueDate)} · Vto.{' '}
							<Box component="span" sx={{ color: overdue ? 'error.main' : 'inherit' }}>
								{formatDate(invoice.dueDate)}
							</Box>
						</Typography>
						{invoice.externalReference && (
							<Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
								Ref: {invoice.externalReference}
							</Typography>
						)}
					</Box>
					<Box sx={{ textAlign: 'right' }}>
						<Typography variant="h6" sx={{ color: invoice.total < 0 ? 'error.main' : 'text.primary' }}>
							{formatAmount(invoice.total, invoice.currency)}
						</Typography>
						{invoice.status !== InvoiceStatusEnum.Draft && invoice.type !== InvoiceTypeEnum.CreditNote && (
							<Typography
								variant="body2"
								sx={{
									color: invoice.pendingAmount <= 0 ? 'success.main' : overdue ? 'error.main' : 'warning.main',
								}}
							>
								{invoice.pendingAmount <= 0
									? 'Saldada'
									: `Saldo ${formatAmount(invoice.pendingAmount, invoice.currency)}`}
							</Typography>
						)}
					</Box>
				</Stack>

				<Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
					{canWrite && isDraft && (
						<Tooltip title="Emitir">
							<IconButton size="small" onClick={() => onIssue(invoice)}>
								<PublishIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					{canWrite && canLink && (
						<Tooltip title="Vincular recibos">
							<IconButton size="small" onClick={() => onLink(invoice)}>
								<AccountBalanceWalletIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					{canWrite && canCreditNote && (
						<Tooltip title="Generar nota de crédito">
							<IconButton size="small" onClick={() => onCreditNote(invoice)}>
								<UndoIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					{canWrite && isDraft && (
						<Tooltip title="Eliminar borrador">
							<IconButton size="small" color="error" onClick={() => onDelete(invoice)}>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					{invoice.pdfUrl && (
						<Tooltip title="Ver PDF">
							<IconButton size="small" onClick={() => window.open(invoice.pdfUrl, '_blank')}>
								<PictureAsPdfIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					<Box sx={{ flex: 1 }} />
					<IconButton size="small" onClick={() => setExpanded((v) => !v)}>
						{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
					</IconButton>
				</Stack>

				<Collapse in={expanded} unmountOnExit>
					<Divider sx={{ my: 1 }} />
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Descripción</TableCell>
								<TableCell align="right">P. unit.</TableCell>
								<TableCell align="right">Cant.</TableCell>
								<TableCell align="right">IVA</TableCell>
								<TableCell align="right">Total</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{invoice.items.map((item) => (
								<TableRow key={item.id}>
									<TableCell>{item.description}</TableCell>
									<TableCell align="right">{formatAmount(item.unitPrice, invoice.currency)}</TableCell>
									<TableCell align="right">{item.quantity}</TableCell>
									<TableCell align="right">{item.taxRate}%</TableCell>
									<TableCell align="right">{formatAmount(item.total, invoice.currency)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{isCredit && (
						<Box sx={{ mt: 2 }}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Cobros vinculados
							</Typography>
							{linkedReceipts.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									Sin cobros vinculados.
								</Typography>
							) : (
								<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
									{linkedReceipts.map((receipt) => (
										<Chip
											key={receipt.id}
											color="success"
											variant="outlined"
											label={`${formatAmount(receipt.transactionAmount, receipt.currency)} · ${
												PLATFORM_LABEL[receipt.platformType]
											}${receipt.authorizationCode ? ` · ${receipt.authorizationCode}` : ''}`}
											onDelete={canWrite ? () => setReceiptToUnlink(receipt) : undefined}
										/>
									))}
								</Stack>
							)}
							<Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
								Cubierto: {formatAmount(invoice.coveredAmount, invoice.currency)} · Saldo:{' '}
								{formatAmount(invoice.pendingAmount, invoice.currency)}
							</Typography>
						</Box>
					)}

					{error && (
						<Alert severity="error" sx={{ mt: 1 }}>
							{error}
						</Alert>
					)}
				</Collapse>
			</CardContent>

			<Dialog open={Boolean(receiptToUnlink)} onClose={() => setReceiptToUnlink(null)}>
				<DialogTitle>Desvincular cobro</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{receiptToUnlink &&
							`¿Desvincular el cobro de ${formatAmount(receiptToUnlink.transactionAmount, receiptToUnlink.currency)} (${
								PLATFORM_LABEL[receiptToUnlink.platformType]
							})? El recibo volverá a quedar libre.`}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setReceiptToUnlink(null)}>Cancelar</Button>
					<Button color="error" variant="contained" onClick={handleUnlink}>
						Desvincular
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};
